import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import "../../styles/Pages.css";
import "../../styles/MyProfile.css";

function MyProfile() {
  const { user } = useContext(AuthContext);
  const [votedPolls, setVotedPolls] = useState([]);
  const [loadingPolls, setLoadingPolls] = useState(false);

  // Check which polls this browser has voted on (via localStorage voterKey)
  useEffect(() => {
    window.scrollTo(0, 0);
    const voterKey = localStorage.getItem("smcVoterKey");
    if (!voterKey) return;

    setLoadingPolls(true);
    fetch(`${process.env.REACT_APP_API_URL}/api/polls/all`)
      .then((r) => r.json())
      .then(async (data) => {
        if (!data.success) return;
        // Check vote status for each poll
        const checks = await Promise.all(
          data.polls.map((poll) =>
            fetch(
              `${process.env.REACT_APP_API_URL}/api/polls/${poll._id}/check-vote?voterKey=${voterKey}`
            )
              .then((r) => r.json())
              .then((vd) => ({ poll, voted: vd.voted, optionIndex: vd.optionIndex }))
              .catch(() => ({ poll, voted: false }))
          )
        );
        setVotedPolls(checks.filter((c) => c.voted));
      })
      .catch(() => {})
      .finally(() => setLoadingPolls(false));
  }, []);

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "U";

  return (
    <div className="page-wrapper">
      <Navbar />
      <div className="page-hero">
        <h1>👤 My Profile</h1>
        <p>Manage your account and view your activity.</p>
      </div>

      <div className="page-content">
        {/* ── Profile Card ── */}
        <div className="profile-card-main">
          <div className="profile-avatar-large">{initials}</div>
          <div className="profile-info-block">
            <h2 className="profile-name">{user?.name || "User"}</h2>
            <span className={`profile-role-badge role-${user?.role}`}>
              {user?.role === "superadmin" ? "⭐ Super Admin" :
               user?.role === "admin" ? "🛡️ Admin" : "🎓 User"}
            </span>
          </div>
        </div>

        {/* ── Account Details ── */}
        <div className="info-card">
          <h2>Account Details</h2>
          <div className="profile-details-grid">
            <div className="profile-detail-item">
              <span className="profile-detail-label">📧 Email</span>
              <span className="profile-detail-value">{user?.email || "—"}</span>
            </div>
            <div className="profile-detail-item">
              <span className="profile-detail-label">📱 Phone</span>
              <span className="profile-detail-value">{user?.phone || "Not Added"}</span>
            </div>
            <div className="profile-detail-item">
              <span className="profile-detail-label">🏛️ Department</span>
              <span className="profile-detail-value">{user?.department || "Not Added"}</span>
            </div>
            <div className="profile-detail-item">
              <span className="profile-detail-label">🔑 Role</span>
              <span className="profile-detail-value" style={{ textTransform: "capitalize" }}>
                {user?.role || "user"}
              </span>
            </div>
          </div>
        </div>

        {/* ── Quick Links ── */}
        <div className="info-card">
          <h2>Quick Links</h2>
          <div className="profile-quick-links">
            <Link to="/dashboard" className="profile-quick-btn">
              📊 Dashboard Overview
            </Link>
            <Link to="/events" className="profile-quick-btn">
              🎉 Events &amp; Polls
            </Link>
            <Link to="/settings" className="profile-quick-btn">
              ⚙️ Settings
            </Link>
            {isAdmin && (
              <>
                <Link to="/admin-dashboard" className="profile-quick-btn admin-btn">
                  🛡️ Admin Panel
                </Link>
                <Link to="/admin/polls" className="profile-quick-btn admin-btn">
                  🗳️ Poll Management
                </Link>
              </>
            )}
          </div>
        </div>

        {/* ── Voting History ── */}
        <div className="info-card">
          <h2>🗳️ Your Voting History</h2>
          {loadingPolls ? (
            <div className="events-loading" style={{ minHeight: "100px" }}>
              <div className="events-spinner" />
              <p>Loading your votes…</p>
            </div>
          ) : votedPolls.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2rem 0", color: "#666" }}>
              <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>🗳️</div>
              <p>You haven&apos;t voted on any polls yet.</p>
              <Link to="/events" className="profile-quick-btn" style={{ display: "inline-block", marginTop: "0.8rem" }}>
                Browse Active Polls →
              </Link>
            </div>
          ) : (
            <div className="voted-polls-list">
              {votedPolls.map(({ poll, optionIndex }) => {
                const isOpen = poll.isActive && new Date(poll.endTime) > new Date();
                return (
                  <div key={poll._id} className="voted-poll-item">
                    <div className="voted-poll-info">
                      <span className={`voted-poll-status ${isOpen ? "open" : "closed"}`}>
                        {isOpen ? "🟢 Live" : "🔴 Ended"}
                      </span>
                      <strong className="voted-poll-title">{poll.title}</strong>
                      <span className="voted-poll-category">{poll.category}</span>
                    </div>
                    <div className="voted-poll-meta">
                      <span className="voted-for-label">Your vote recorded ✔</span>
                      <span className="voted-poll-date">
                        Ends: {new Date(poll.endTime).toLocaleDateString("en-IN")}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default MyProfile;